import { LocalizedPipe } from 'src/app/shared/localized.pipe';
import { resourceText } from 'src/app/shared/services/localization';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { TenantService } from 'src/app/tenant/tenant.service';
import { UserMessageComponent } from 'src/app/shared/messages/user-message/user-message.component';

@Component({
  selector: 'app-email-verification-reminder',
  standalone: true,
  imports: [LocalizedPipe, CommonModule, RouterModule, UserMessageComponent],
  template: `
    <app-user-message *ngIf="(email || pendingEmail) && (profileMode || ((!verified || pendingEmail) && !dismissed))" type="info" [title]="pendingEmail ? 'Email change awaiting verification' : verified ? 'Email verified' : 'Verify your email'" class="email-reminder" [dismissible]="false">
      <div *ngIf="email">{{ email }} · {{ verified ? ('VERIFIED' | translate) : ('NOT_VERIFIED' | translate) }}</div>
      <div *ngIf="pendingEmail"><strong>{{ pendingEmail }}</strong> {{ 'AWAITING_VERIFICATION_YOUR_CURRENT_EMAIL_STAYS_ACTIVE' | translate }}<span *ngIf="pendingExpired"> {{ 'THE_LINK_EXPIRED_REQUEST_A_NEW_ONE' | translate }}</span></div>
      <div class="email-actions" *ngIf="pendingEmail || !verified">
        <button type="button" class="btn btn-primary" [disabled]="sending || cooldown" (click)="resend()">{{ sending ? ('SENDING' | translate) : cooldown ? ('LINK_SENT' | translate) : ('SEND_VERIFICATION_EMAIL' | translate) }}</button>
        <a [routerLink]="profileLink" fragment="email" class="btn btn-link">{{ 'CHANGE_EMAIL' | translate }}</a>
        <button *ngIf="pendingEmail" type="button" class="btn btn-link" [disabled]="sending" (click)="cancelChange()">{{ 'CANCEL_CHANGE' | translate }}</button>
        <button *ngIf="!profileMode" type="button" class="btn btn-link" (click)="snooze()">{{ 'REMIND_ME_LATER' | translate }}</button>
      </div>
      <small role="status">{{ message }}</small>
    </app-user-message>`,
  styles: [`
    :host { display:block; }
    .email-reminder { display:block; margin:0 0 12px; overflow-wrap:anywhere; }
    .email-actions { display: flex; gap: .5rem; flex-wrap: wrap; }
    .email-actions .btn { padding: .4rem .6rem; font-size: .85rem; }
    .email-actions .btn-link { color: inherit; text-decoration: underline; }
  `]
})
export class EmailVerificationReminderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() profileMode = false;
  @Input() refreshKey = '';
  @Input() profileUserId = '';
  pendingEmail = '';
  pendingExpired = false;
  email = '';
  verified = true;
  dismissed = false;
  sending = false;
  cooldown = false;
  message = '';
  private userId = '';
  private subscriptions = new Subscription();
  private request?: Subscription;
  private resendRequest?: Subscription;
  private timer?: ReturnType<typeof setTimeout>;

  private get snoozeKey() { return 'email-verification-reminder:' + this.userId + ':' + (this.pendingEmail || this.email).trim().toLowerCase(); }

  ngOnChanges() { this.refresh(); }

  cancelChange() {
    if (this.sending) return;
    this.sending = true;
    this.resendRequest = this.http.post(environment.apiHost + '/User/CancelEmailChange', null).subscribe({
      next: () => { this.resetResend(); this.message = resourceText('EMAIL_CHANGE_CANCELLED_YOUR_CURRENT_EMAIL_IS_UNCHANGED'); this.refresh(); },
      error: () => { this.sending = false; this.message = resourceText('UNABLE_TO_CANCEL_THE_CHANGE_PLEASE_TRY_AGAIN'); }
    });
  }

  snooze() {
    this.dismissed = true;
    try { localStorage.setItem(this.snoozeKey, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch { /* Storage may be unavailable. */ }
  }

  constructor(private auth: AuthService, private http: HttpClient, private router: Router, private tenantService: TenantService) {}

  get profileLink(): string[] {
    return ['/', this.tenantService.getTenant(), 'myprofile'];
  }

  ngOnInit() {
    this.subscriptions.add(this.auth.user.subscribe(user => {
      const nextId = user?.id || '';
      if (nextId !== this.userId) {
        this.resetResend();
        this.dismissed = false;
        this.message = '';
        this.email = '';
        this.pendingEmail = '';
        this.verified = true;
      }
      this.userId = nextId;
      this.refresh();
    }));
    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.refresh();
    }));
  }

  private refresh() {
    this.request?.unsubscribe();
    if (!this.userId || (this.profileMode && this.profileUserId !== this.userId)) { this.email = ''; return; }
    this.request = this.http.get<{ email: string; emailConfirmed: boolean; pendingEmail?: string; pendingExpired?: boolean }>(environment.apiHost + '/User/EmailVerificationStatus').subscribe({
      next: status => {
        if (this.email !== status.email || this.pendingEmail !== (status.pendingEmail || '')) { this.resetResend(); this.dismissed = false; this.message = ''; }
        this.email = status.email || '';
        this.pendingEmail = status.pendingEmail || '';
        this.pendingExpired = !!status.pendingExpired;
        this.verified = status.emailConfirmed;
        try { this.dismissed = Number(localStorage.getItem(this.snoozeKey) || 0) > Date.now(); } catch { /* Keep the reminder usable without storage. */ }
      },
      error: () => { /* A reminder must never block navigation. */ }
    });
  }

  private resetResend() {
    this.resendRequest?.unsubscribe();
    clearTimeout(this.timer);
    this.sending = false;
    this.cooldown = false;
  }

  resend() {
    if (this.sending || this.cooldown) return;
    this.sending = true;
    const endpoint = this.pendingEmail ? '/User/ResendEmailChange' : '/User/resendActivationEmail?UserId=' + encodeURIComponent(this.userId);
    this.resendRequest = this.http.post(environment.apiHost + endpoint, null).subscribe({
      next: () => {
        this.sending = false;
        this.cooldown = true;
        this.pendingExpired = false;
        this.message = resourceText('VERIFICATION_LINK_SENT_PLEASE_CHECK_YOUR_INBOX_AND_SPAM_FOLDER');
        this.timer = setTimeout(() => this.cooldown = false, 60000);
      },
      error: () => { this.sending = false; this.message = resourceText('UNABLE_TO_RESEND_RIGHT_NOW_PLEASE_TRY_AGAIN_SHORTLY'); }
    });
  }

  ngOnDestroy() {
    this.resetResend();
    this.subscriptions.unsubscribe();
    this.request?.unsubscribe();
    clearTimeout(this.timer);
  }
}
